import React from 'react';
import { Box, Typography } from '@mui/material';
import { Image, Text, View } from '@react-pdf/renderer';
import {
  PVM_PAGE2_LEFT_IMAGE_GAP,
  PVM_PAGE2_LEFT_IMAGE_LABEL,
  PVM_PAGE2_LEFT_IMAGE_LABEL_2,
  PVM_PAGE2_LEFT_IMAGE_LABEL_3,
  PVM_PAGE2_LEFT_IMAGE_MAX_HEIGHT,
  PVM_PAGE2_LEFT_IMAGE_WIDTH,
} from './templateConstants';
import { PVM_HEADLINE_COLOR, PVM_TEXT_COLOR, withPvmBodyStyle, withPvmHeadlineStyle } from './pvmBranding';

/** Preview scale ≈ PDF pts × 1.35 so web matches react-pdf sizing. */
const WEB_IMG_W = Math.round(PVM_PAGE2_LEFT_IMAGE_WIDTH * 1.35);
const WEB_PLACEHOLDER_H = Math.round(PVM_PAGE2_LEFT_IMAGE_MAX_HEIGHT * 1.35);
const WEB_ROW_GAP = Math.round(PVM_PAGE2_LEFT_IMAGE_GAP * 4);

const defaultTitleSx = () =>
  withPvmHeadlineStyle(
    {
      fontSize: 14,
      fontWeight: 700,
      color: PVM_HEADLINE_COLOR,
      borderBottom: `2px solid ${PVM_HEADLINE_COLOR}`,
      pb: 0.5,
      mb: 0.75,
    } as Record<string, unknown>,
    true,
  );

const bodySx = withPvmBodyStyle(
  {
    fontSize: 12,
    color: PVM_TEXT_COLOR,
    lineHeight: 1.35,
    textAlign: 'justify' as const,
    mb: 1.5,
  },
  true,
);

/**
 * Fixed width, natural height — each award image keeps its aspect ratio.
 * Empty placeholders use a shared min-height so the slot stays clickable.
 */
const ImageSlotWeb: React.FC<{
  imageUrl?: string | null;
  placeholder: string;
  slotId: string;
}> = ({ imageUrl, placeholder, slotId }) => (
  <Box
    id={slotId}
    sx={{
      width: WEB_IMG_W,
      maxWidth: '32%',
      boxSizing: 'border-box',
      flexShrink: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...(imageUrl
        ? { backgroundColor: '#fff', height: 'auto' }
        : {
            border: '1px dashed #a7a8ab',
            backgroundColor: '#f5f5f5',
            px: 0.75,
            py: 0.5,
            minHeight: WEB_PLACEHOLDER_H,
          }),
    }}
  >
    {imageUrl ? (
      <Box
        component="img"
        src={imageUrl}
        alt={placeholder}
        sx={{
          display: 'block',
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          imageRendering: '-webkit-optimize-contrast',
        }}
      />
    ) : (
      <Typography
        sx={{
          fontSize: 10,
          color: '#808080',
          textAlign: 'center',
          lineHeight: 1.3,
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
        }}
      >
        {placeholder}
      </Typography>
    )}
  </Box>
);

const ImageSlotPdf: React.FC<{
  imageSrc?: string;
  placeholder: string;
  pdfStyles: any;
}> = ({ imageSrc, placeholder, pdfStyles }) => (
  <View
    style={{
      width: PVM_PAGE2_LEFT_IMAGE_WIDTH,
      alignItems: 'center',
      justifyContent: 'center',
      ...(imageSrc
        ? { backgroundColor: '#ffffff' }
        : {
            height: PVM_PAGE2_LEFT_IMAGE_MAX_HEIGHT,
            borderWidth: 1,
            borderColor: '#a7a8ab',
            borderStyle: 'dashed',
            backgroundColor: '#f5f5f5',
            padding: 4,
          }),
    }}
  >
    {imageSrc ? (
      <Image
        src={imageSrc}
        style={{
          width: PVM_PAGE2_LEFT_IMAGE_WIDTH,
        }}
      />
    ) : (
      <Text style={{ ...pdfStyles.sectionText, fontSize: 6.5, color: '#808080', textAlign: 'center' }}>
        {placeholder}
      </Text>
    )}
  </View>
);

/** Page 2 left: CHANCEN → RISIKEN only (award images sit in a row above Disclaimer). */
export const PvmPage2LeftWeb: React.FC<{
  chancen?: string;
  risiken?: string;
  chancenTitle?: string;
  risikenTitle?: string;
  titleSx?: (theme?: unknown) => object;
  onTitleClick?: () => void;
}> = ({
  chancen = '',
  risiken = '',
  chancenTitle = 'CHANCEN',
  risikenTitle = 'RISIKEN',
  titleSx,
  onTitleClick,
}) => {
  const resolvedTitleSx = titleSx || defaultTitleSx;

  return (
    <Box id="pvm-page2-left-column" sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Box id="chancen-section" sx={{ mb: 1.5 }}>
        <Box sx={resolvedTitleSx} onClick={onTitleClick}>
          {chancenTitle}
        </Box>
        <Box sx={bodySx}>{chancen}</Box>
      </Box>

      <Box id="risiken-section" sx={{ mb: 0.5 }}>
        <Box sx={resolvedTitleSx} onClick={onTitleClick}>
          {risikenTitle}
        </Box>
        <Box sx={bodySx}>{risiken}</Box>
      </Box>
    </Box>
  );
};

/** Full-width row of 3 award images above Disclaimer (same width each, natural height). */
export const PvmPage2AwardImagesRowWeb: React.FC<{
  imageUrl?: string | null;
  imageUrl2?: string | null;
  imageUrl3?: string | null;
}> = ({ imageUrl, imageUrl2, imageUrl3 }) => (
  <Box
    id="pvm-page2-award-images-row"
    sx={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: `${WEB_ROW_GAP}px`,
      width: '100%',
      mt: 4,
      mb: 0,
    }}
  >
    <ImageSlotWeb
      slotId="pvm-page2-left-image"
      imageUrl={imageUrl}
      placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL}
    />
    <ImageSlotWeb
      slotId="pvm-page2-left-image-2"
      imageUrl={imageUrl2}
      placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL_2}
    />
    <ImageSlotWeb
      slotId="pvm-page2-left-image-3"
      imageUrl={imageUrl3}
      placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL_3}
    />
  </Box>
);

export const PvmPage2LeftPdf: React.FC<{
  pdfStyles: any;
  chancen?: string;
  risiken?: string;
  chancenTitle?: string;
  risikenTitle?: string;
  templateColor?: string;
  renderContentHeader?: (title: string, color?: string) => React.ReactNode;
}> = ({
  pdfStyles,
  chancen = '',
  risiken = '',
  chancenTitle = 'CHANCEN',
  risikenTitle = 'RISIKEN',
  templateColor = PVM_HEADLINE_COLOR,
  renderContentHeader,
}) => {
  const header = (title: string) =>
    renderContentHeader ? (
      renderContentHeader(title, templateColor)
    ) : (
      <Text style={[pdfStyles.sectionTitle, { color: templateColor }]}>{title}</Text>
    );

  return (
    <View style={{ width: '100%' }}>
      <View style={[pdfStyles.leftColumnSection, { marginBottom: 3 }]}>
        {header(chancenTitle)}
        <Text style={[pdfStyles.sectionText, { marginBottom: 0, color: PVM_TEXT_COLOR }]}>{chancen}</Text>
      </View>

      <View style={[pdfStyles.leftColumnSection, { marginBottom: 3 }]}>
        {header(risikenTitle)}
        <Text style={[pdfStyles.sectionText, { marginBottom: 0, color: PVM_TEXT_COLOR }]}>{risiken}</Text>
      </View>
    </View>
  );
};

export const PvmPage2AwardImagesRowPdf: React.FC<{
  pdfStyles: any;
  imageSrc?: string;
  imageSrc2?: string;
  imageSrc3?: string;
}> = ({ pdfStyles, imageSrc, imageSrc2, imageSrc3 }) => (
  <View
    style={{
      width: '100%',
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginTop: 20,
      marginBottom: 0,
    }}
  >
    <ImageSlotPdf pdfStyles={pdfStyles} imageSrc={imageSrc} placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL} />
    <ImageSlotPdf pdfStyles={pdfStyles} imageSrc={imageSrc2} placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL_2} />
    <ImageSlotPdf pdfStyles={pdfStyles} imageSrc={imageSrc3} placeholder={PVM_PAGE2_LEFT_IMAGE_LABEL_3} />
  </View>
);
